import React, { useEffect, useRef, useState } from 'react';
import { MessageCircleIcon } from 'lucide-react';
import { DS } from '../ds';
import { GradientText } from '../Visuals';
interface FinalCTAProps {
  onCTA: (id: string) => void;
}
export const FinalCTA = ({
  onCTA
}: FinalCTAProps) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} id="final-cta" style={{
    background: DS.color.ivory,
    position: 'relative',
    overflow: 'hidden',
    padding: '96px 24px'
  }} data-id="element-412" data-mp="38t7hjgh8opah">
      {/* Textura beige sutil */}
      <div aria-hidden="true" style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23B5894F' fill-opacity='0.018'%3E%3Ccircle cx='40' cy='40' r='2'/%3E%3C/g%3E%3C/svg%3E\")",
      pointerEvents: 'none'
    }} data-id="element-413" data-mp="171up3qwgk9vb" />

      {/* Borde decorativo superior */}
      <div aria-hidden="true" style={{
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '320px',
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${DS.color.aqua}55, transparent)`
    }} data-id="element-414" data-mp="78ziu4aqx1wg" />

      {/* Card central con fondo blanco */}
      <div style={{
      maxWidth: '780px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 1
    }} data-id="element-415" data-mp="1rpmf9th7j0n8">
        <div style={{
        background: DS.color.white,
        borderRadius: '32px',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(11,31,36,0.10)',
        border: `1px solid ${DS.color.border}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.8s ${DS.ease.out}, transform 0.8s ${DS.ease.out}`
      }} data-id="element-416" data-mp="1mq02fl5ajmey">
          {/* Franja teal superior */}
          <div style={{
          background: `linear-gradient(135deg, ${DS.color.tealDeep} 0%, #07202a 100%)`,
          padding: '52px 52px 52px',
          position: 'relative',
          overflow: 'hidden'
        }} data-id="element-417" data-mp="1ogmfrklhx48">
            {/* Grid CAD */}
            <div aria-hidden="true" style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(75,199,210,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(75,199,210,0.05) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            pointerEvents: 'none'
          }} data-id="element-418" data-mp="ffbnwisrlbxs" />

            {/* Glow */}
            <div aria-hidden="true" style={{
            position: 'absolute',
            top: '-60px',
            right: '-60px',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(ellipse, rgba(75,199,210,0.12) 0%, transparent 65%)',
            pointerEvents: 'none'
          }} data-id="element-419" data-mp="31kncff93u3m5" />

            {/* Badge */}
            <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 16px',
            borderRadius: '100px',
            background: 'rgba(75,199,210,0.10)',
            border: '1px solid rgba(75,199,210,0.22)',
            marginBottom: '24px',
            position: 'relative',
            zIndex: 1
          }} data-id="element-420" data-mp="2isuk7n1v0mfh">
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true" data-id="element-421" data-mp="csbdib4kbneq">
                <circle cx="4" cy="4" r="4" fill={DS.color.aqua} data-id="element-422" data-mp="409duf2bib5i" />
              </svg>
              <span style={{
              fontFamily: DS.font.body,
              fontSize: '11px',
              fontWeight: 700,
              color: DS.color.aquaLight,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }} data-id="element-423" data-mp="3l952hsom61x9">
                Empieza hoy
              </span>
            </div>

            {/* Headline */}
            <h2 style={{
            fontFamily: DS.font.heading,
            fontWeight: 700,
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: '#fff',
            lineHeight: 1.15,
            letterSpacing: '-0.03em',
            margin: '0 0 8px',
            position: 'relative',
            zIndex: 1
          }} data-id="element-424" data-mp="1aiodbwbqt8l4">
              ¿Listo para enviar{' '}
              <GradientText data-id="element-425" data-mp="1tm00y67x07uc">tu primer caso?</GradientText>
            </h2>

            {/* Subtítulo dentro de la franja */}
            <p style={{
            fontFamily: DS.font.body,
            fontSize: '15px',
            color: 'rgba(255,255,255,0.55)',
            lineHeight: 1.6,
            marginTop: '14px',
            marginBottom: '0',
            maxWidth: '520px',
            position: 'relative',
            zIndex: 1
          }} data-id="element-426" data-mp="28a383ixp1pkq">
              El aliado tecnológico para ortodoncia invisible en Colombia.
              Precisión, respaldo INVIMA y kit eco-premium en cada caso.
            </p>
          </div>

          {/* Parte inferior ivory */}
          <div style={{
          padding: '36px 52px 44px'
        }} data-id="element-427" data-mp="2i1rcd81xwnbj">
            <p style={{
            fontFamily: DS.font.body,
            fontSize: '16px',
            color: DS.color.body,
            lineHeight: 1.7,
            marginBottom: '32px',
            maxWidth: '560px'
          }} data-id="element-428" data-mp="2mzsyk1unx2r8">
              Únete a los odontólogos y ortodoncistas que ya ofrecen
              ortodoncia invisible con tecnología de precisión y una
              presentación que diferencia su clínica.
            </p>

            {/* Botones */}
            <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            alignItems: 'center',
            marginBottom: '28px'
          }} data-id="element-429" data-mp="3ap3p6qiatgrn">
              {/* Botón primario */}
              <button type="button" onClick={() => onCTA('contacto')} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              padding: '15px 32px',
              borderRadius: '100px',
              background: `linear-gradient(135deg, ${DS.color.teal}, ${DS.color.tealDeep})`,
              color: '#fff',
              fontFamily: DS.font.body,
              fontSize: '15px',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: `all 0.25s ${DS.ease.smooth}`,
              boxShadow: DS.shadow.glowSm,
              whiteSpace: 'nowrap'
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = DS.shadow.glow;
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = DS.shadow.glowSm;
            }} data-id="element-430" data-mp="3oov9ta3blvem">
                Enviar mi primer caso
              </button>

              {/* Botón WhatsApp */}
              <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              padding: '15px 28px',
              borderRadius: '100px',
              border: `1.5px solid ${DS.color.teal}50`,
              background: DS.color.mint,
              color: DS.color.tealDeep,
              fontFamily: DS.font.body,
              fontSize: '15px',
              fontWeight: 700,
              textDecoration: 'none',
              transition: `all 0.25s ${DS.ease.smooth}`,
              whiteSpace: 'nowrap'
            }} onMouseEnter={e => {
              e.currentTarget.style.background = `${DS.color.aqua}20`;
              e.currentTarget.style.borderColor = DS.color.teal;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }} onMouseLeave={e => {
              e.currentTarget.style.background = DS.color.mint;
              e.currentTarget.style.borderColor = `${DS.color.teal}50`;
              e.currentTarget.style.transform = 'translateY(0)';
            }} data-id="element-431" data-mp="1a2vc4m6nf7t0">
                <MessageCircleIcon size={17} aria-hidden="true" data-id="element-432" data-mp="3m8fsie5i9lyt" />
                Continuar por WhatsApp
              </a>
            </div>

            {/* Nota de confianza */}
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap'
          }} data-id="element-433" data-mp="1px1ba67rt6">
              {['Sin compromisos', 'Respuesta en menos de 24h', 'Colombia'].map((item, i) => <div key={item} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }} data-id="element-434" data-mp="28udau9a5l916" data-mpi={i}>
                  {i > 0 && <span aria-hidden="true" style={{
                width: '3px',
                height: '3px',
                borderRadius: '50%',
                background: DS.color.border,
                display: 'inline-block',
                marginRight: '14px'
              }} data-id="element-435" data-mp="1yytddifxy2ab" />}
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" data-id="element-436" data-mp="1b74hjs3wuvom">
                    <circle cx="7" cy="7" r="6" stroke={DS.color.aqua} strokeWidth="1.2" opacity="0.6" data-id="element-437" data-mp="3irl76znytzhn" />
                    <path d="M4.5 7l2 2 3-3" stroke={DS.color.teal} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" data-id="element-438" data-mp="mzx688xdvnk" />
                  </svg>
                  <span style={{
                fontFamily: DS.font.body,
                fontSize: '13px',
                color: DS.color.muted
              }} data-id="element-439" data-mp="341dtex8wl4na">
                    {item}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
};